import React, { useState, useEffect } from 'react'
import SEOHead from '../components/SEOHead'
import ImageLightbox from '../components/ImageLightbox'
import './InventoryPage.css'

interface TrailerInventoryItem {
  id: number
  model_name: string
  category_id: number
  category_name: string
  type: string
  material: string
  axle_configuration: string
  capacity_pounds: number
  length_feet: number
  beam_width_inches: number
  price: number
  stock_quantity: number
  is_available: boolean
  image_url: string
  image_urls: string[]
  description: string
  features: string[]
  specifications: Record<string, string>
  boat_length: string
  notes: string
  created_at: string
  updated_at: string
}

const InventoryPage: React.FC = () => {
  const [inventory, setInventory] = useState<TrailerInventoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set())
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxImages, setLightboxImages] = useState<string[]>([])
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const toggleExpand = (itemId: number) => {
    setExpandedCards(prev => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId)
      } else {
        newExpanded.add(itemId)
      }
      return newExpanded
    })
  }

  const openLightbox = (images: string[], index: number = 0) => {
    setLightboxImages(images)
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  useEffect(() => {
    fetchInventory()

    // Listen for visibility change to refresh when user comes back to the page
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchInventory()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Listen for inventory update events from InventoryManagement component
    const handleInventoryUpdate = () => {
      fetchInventory()
    }

    window.addEventListener('inventory-updated', handleInventoryUpdate as EventListener)

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('inventory-updated', handleInventoryUpdate as EventListener)
    }
  }, [])

  const fetchInventory = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/inventory')
      
      if (!response.ok) {
        throw new Error(`Failed to fetch inventory: ${response.statusText}`)
      }
      
      const data = await response.json()
      setInventory(data)
    } catch (err) {
      setError('Failed to fetch inventory data')
      console.error('Error fetching inventory:', err)
    } finally {
      setLoading(false)
    }
  }

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || item.category_name.toLowerCase().includes(selectedCategory.toLowerCase())
    return matchesSearch && matchesCategory && item.is_available && item.stock_quantity > 0
  })

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'aluminum', label: 'Aluminum Trailers' },
    { value: 'galvanized', label: 'Galvanized Trailers' },
    { value: 'bunk', label: 'Bunk Trailers' },
    { value: 'roller', label: 'Roller Trailers' },
    { value: 'pwc', label: 'PWC Trailers' },
    { value: 'pontoon', label: 'Pontoon Trailers' },
    { value: 'commander', label: 'Commander Trailers' },
    { value: 'skiff', label: 'Skiff Trailers' },
    { value: 'tritoon', label: 'Tritoon Trailers' }
  ]

  if (loading) {
    return (
      <div className="inventory-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading inventory...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="inventory-page">
        <div className="error-container">
          <h2>Error Loading Inventory</h2>
          <p>{error}</p>
          <button onClick={fetchInventory} className="retry-btn">Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className="inventory-page">
      <SEOHead
        title="Boat Trailer Inventory - Available Trailers in Stock | Fast Boat Trailers"
        description="Browse our current boat trailer inventory at Fast Boat Trailers in Edgewater, MD. View available trailers with photos, specifications, and pricing. Updated daily with new stock."
        keywords="boat trailer inventory, trailers in stock Maryland, available trailers Edgewater, trailer stock, boat trailers for sale"
        ogTitle="Boat Trailer Inventory - Available Trailers in Stock"
        ogDescription="Browse our current boat trailer inventory. View available trailers with photos, specifications, and pricing. Updated daily."
        ogImage="/images/logos/fbt-logo-3-noborder.jpeg"
        canonical="https://fastboattrailers.com/inventory"
      />
      <div className="inventory-header">
        <h1>Trailer Inventory</h1>
        <p>Browse our current in-stock trailer selection</p>
      </div>

      <div className="inventory-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search trailers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="category-filter">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-select"
          >
            {categories.map(category => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="inventory-summary">
        <p>{filteredInventory.length} trailer{filteredInventory.length !== 1 ? 's' : ''} in stock</p>
      </div>

      <div className="inventory-list">
        {filteredInventory.map(item => (
          <div key={item.id} className="inventory-card">
            <div className="inventory-image">
              {item.image_urls && item.image_urls.length > 0 ? (
                <div
                  className="image-gallery"
                  onClick={() => openLightbox(item.image_urls, 0)}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={item.image_urls[0]}
                    alt={item.model_name}
                    className="main-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/images/logos/fbt-logo-3-noborder.jpeg';
                    }}
                  />
                </div>
              ) : (
                <img
                  src={item.image_url || '/images/logos/fbt-logo-3-noborder.jpeg'}
                  alt={item.model_name}
                  onClick={() => openLightbox([item.image_url || '/images/logos/fbt-logo-3-noborder.jpeg'], 0)}
                  style={{ cursor: 'pointer' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/logos/fbt-logo-3-noborder.jpeg';
                  }}
                />
              )}
              <div className="stock-badge">
                {item.stock_quantity} in stock
              </div>
            </div>
            <div className="inventory-content">
              <div className="inventory-main">
                <div className="inventory-details">
                  <h3>Venture {item.model_name}</h3>
                  <div className="quick-specs">
                    <div className="spec-item">
                      <span className="spec-label">Material:</span>
                      <span className="spec-value">{item.material}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Boat Length:</span>
                      <span className="spec-value">{item.boat_length || 'N/A'}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Trailer Length:</span>
                      <span className="spec-value">{item.length_feet} ft</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Capacity:</span>
                      <span className="spec-value">{Number(item.capacity_pounds).toLocaleString('en-US')} lbs</span>
                    </div>
                  </div>
                  {item.notes && (
                    <div className="inventory-notes">
                      <span className="notes-label">Notes:</span>
                      <span className="notes-value">{item.notes}</span>
                    </div>
                  )}
                </div>
                <div className="inventory-price">
                  ${Number(item.price).toLocaleString('en-US')}
                </div>
              </div>

              <div className={`inventory-expanded ${expandedCards.has(item.id) ? 'expanded' : ''}`}>
                <p className="inventory-description">{item.description}</p>

                <div className="inventory-specs">
                  <div className="spec-row">
                    <span className="spec-label">Beam Width:</span>
                    <span className="spec-value">{item.beam_width_inches} in</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Type:</span>
                    <span className="spec-value">{item.type}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Axle:</span>
                    <span className="spec-value">{item.axle_configuration}</span>
                  </div>
                  {item.specifications.tire_size && (
                    <div className="spec-row">
                      <span className="spec-label">Tire Size:</span>
                      <span className="spec-value">{item.specifications.tire_size}</span>
                    </div>
                  )}
                  {item.specifications.brake_type && (
                    <div className="spec-row">
                      <span className="spec-label">Brakes:</span>
                      <span className="spec-value">{item.specifications.brake_type}</span>
                    </div>
                  )}
                </div>

                {item.features && item.features.length > 0 && (
                  <div className="inventory-features">
                    <h4>Features:</h4>
                    <ul>
                      {item.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="inventory-actions">
                <button
                  className="expand-btn"
                  onClick={() => toggleExpand(item.id)}
                >
                  {expandedCards.has(item.id) ? 'Show Less' : 'Show More'}
                </button>
                <a href="tel:410-570-9150" className="contact-btn call-btn">Call Us</a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredInventory.length === 0 && (
        <div className="no-results">
          <h3>No trailers found</h3>
          <p>Try adjusting your search criteria or check back later for new inventory.</p>
        </div>
      )}

      {lightboxOpen && (
        <ImageLightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          altText="Trailer"
        />
      )}
    </div>
  )
}

export default InventoryPage